import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TokenStorageService } from './token-storage.service';

/**
 * Decodifica el payload de un token JWT y verifica si su fecha de expiración ha pasado.
 *
 * @param token El token JWT en formato string.
 * @returns `true` si el token ha expirado o es inválido, `false` si sigue vigente.
 */
const isTokenExpired = (token: string): boolean => {
  try {
    // 1. Un JWT tiene 3 partes separadas por puntos: header.payload.signature
    // Nos interesa el payload (índice 1), que contiene los datos del usuario y la caducidad.
    const payloadBase64Url = token.split('.')[1];

    // 2. Reemplazamos caracteres para convertir de Base64Url (estándar web) a Base64 normal
    const payloadBase64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');

    // 3. atob() decodifica el Base64 a texto, y JSON.parse lo convierte en un objeto JS
    const payload = JSON.parse(atob(payloadBase64));

    // 4. El campo 'exp' del JWT viene en segundos. Date.now() da milisegundos.
    const expirationTimeInSeconds = payload.exp;
    const currentTimeInSeconds = Math.floor(Date.now() / 1000);

    // Retorna true si la hora actual superó a la hora de expiración
    return currentTimeInSeconds >= expirationTimeInSeconds;
  } catch {
    // Si ocurre cualquier error (ej. el token no tiene formato JWT), lo damos por inválido
    return true;
  }
};

/**
 * Guardia de ruta funcional para proteger el acceso a vistas que requieren autenticación.
 *
 * Verifica la existencia y la validez criptográfica (fecha de expiración) de un token
 * de sesión a través de `TokenStorageService`. Si no hay token, o si este ya ha caducado
 * (`exp`), evita la carga del componente, previniendo errores 401 innecesarios contra
 * el backend y redirigiendo al usuario de forma segura al login.
 *
 * @returns `true` si el token existe y es válido, o un `UrlTree` para redirigir a `/login`.
 *
 * @example
 * // Uso en app.routes.ts:
 * export const routes: Routes = [
 * {
 * path: 'dashboard',
 * canActivate: [authGuard],
 * loadComponent: () => import('./dashboard.component').then(m => m.DashboardComponent)
 * }
 * ];
 */
export const authGuard: CanActivateFn = () => {
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);
  const token = tokenStorage.getToken();

  // Verificamos que el token exista Y que NO esté expirado
  if (token && !isTokenExpired(token)) {
    return true;
  }

  // Opcional pero recomendado: Si hay token pero está caducado, limpiamos la basura
  if (token) {
    // Si tu servicio tiene un método removeToken(), este es el lugar para llamarlo
    // tokenStorage.removeToken();
  }

  return router.createUrlTree(['/login']);
};
